import {
  getLeoUuid,
  LeoUUID,
  getObject,
  getString,
  getBoolean,
  getOptionalObject,
  LeoRPCRequest,
  LeoRPCResponse,
  LeoRPCError,
  LeoRPCResult,
  LeoRPC
} from "@surya-digital/leo-ts-types";
import { RespondentQuestionDetails, RespondentQuestionDetailsDTO } from "./respondentQuestionDetails";
import { ProgressPercentage, ProgressPercentageDTO } from "./progressPercentage";
import { SurveyLocalizedText, SurveyLocalizedTextDTO } from "../assets/surveyLocalizedText";
/* eslint-disable import/export */
export abstract class BacktrackToFirstUnansweredQuestionRPC implements
  LeoRPC<BacktrackToFirstUnansweredQuestionRPC.Request, BacktrackToFirstUnansweredQuestionRPC.Response, BacktrackToFirstUnansweredQuestionRPC.Errors.Errors>
{
  abstract execute(
    request: BacktrackToFirstUnansweredQuestionRPC.Request
  ): Promise<LeoRPCResult<BacktrackToFirstUnansweredQuestionRPC.Response, BacktrackToFirstUnansweredQuestionRPC.Errors.Errors>>;
}

export namespace BacktrackToFirstUnansweredQuestionRPC {

  export class InvalidRequestError extends Error {
    constructor(message?: string) {
      super(message);
      Object.setPrototypeOf(this, new.target.prototype);
      this.name = "InvalidRequestError";
    }
  }

  export interface RequestDTO {
    surveyLinkId: string;
  }
  
  export class Request implements LeoRPCRequest {
    readonly isBacktrackToFirstUnansweredQuestionRPCRequest: boolean = true;
  
    readonly surveyLinkId: LeoUUID;
  
  
    constructor(
    surveyLinkId: LeoUUID
    ) {
      this.surveyLinkId = surveyLinkId;
    }
  
    static fromDTO(dto: RequestDTO): Request {
      const surveyLinkId = getLeoUuid(dto, "surveyLinkId", InvalidRequestSurveyLinkIdError);
      return new Request(
        surveyLinkId
      );
    }
  
    toDTO(): RequestDTO {
      const dto: RequestDTO = {
          surveyLinkId: this.surveyLinkId.toDTO()
      };
      return dto;
    }
    }
  
  Request.prototype.toString = function toString(): string {
    return `BacktrackToFirstUnansweredQuestionRPC(surveyLinkId='${this.surveyLinkId}')`;
  };
  

  export class InvalidResponseError extends Error {
    constructor(message?: string) {
      super(message);
      Object.setPrototypeOf(this, new.target.prototype);
      this.name = "InvalidResponseError";
    }
  }

  export interface ResponseDTO {
    respondentQuestionDetails: RespondentQuestionDetailsDTO;
    answerJson: string;
    isFirstQuestion: boolean;
    progressPercentage: ProgressPercentageDTO;
    submitConfirmationButtonText?: SurveyLocalizedTextDTO | null;
  }
  
  export class Response implements LeoRPCResponse {
    readonly isBacktrackToFirstUnansweredQuestionResponse: boolean = true;
  
    readonly respondentQuestionDetails: RespondentQuestionDetails;
    readonly answerJson: string;
    readonly isFirstQuestion: boolean;
    readonly progressPercentage: ProgressPercentage;
    readonly submitConfirmationButtonText: SurveyLocalizedText | null;
  
  
    constructor(
    respondentQuestionDetails: RespondentQuestionDetails, 
    answerJson: string, 
    isFirstQuestion: boolean, 
    progressPercentage: ProgressPercentage, 
    submitConfirmationButtonText: SurveyLocalizedText | null = null
    ) {
      this.respondentQuestionDetails = respondentQuestionDetails;
      Response.validateAnswerJson(answerJson);
      this.answerJson = answerJson;
      this.isFirstQuestion = isFirstQuestion;
      this.progressPercentage = progressPercentage;
      this.submitConfirmationButtonText = submitConfirmationButtonText;
    }
  
    static fromDTO(dto: ResponseDTO): Response {
      const respondentQuestionDetails = RespondentQuestionDetails.fromDTO(getObject(dto, "respondentQuestionDetails", InvalidResponseRespondentQuestionDetailsError));
      const answerJson = getString(dto, "answerJson", InvalidResponseAnswerJsonError);
      const isFirstQuestion = getBoolean(dto, "isFirstQuestion", InvalidResponseIsFirstQuestionError);
      const progressPercentage = ProgressPercentage.fromDTO(getObject(dto, "progressPercentage", InvalidResponseProgressPercentageError));
      let submitConfirmationButtonText = getOptionalObject(dto, "submitConfirmationButtonText");
      if(submitConfirmationButtonText !== undefined && submitConfirmationButtonText !== null) {
        submitConfirmationButtonText = SurveyLocalizedText.fromDTO(getObject(dto, "submitConfirmationButtonText", InvalidResponseSubmitConfirmationButtonTextError));
      }
      return new Response(
        respondentQuestionDetails,
        answerJson,
        isFirstQuestion,
        progressPercentage,
        submitConfirmationButtonText
      );
    }
  
    toDTO(): ResponseDTO {
      const dto: ResponseDTO = {
          respondentQuestionDetails: this.respondentQuestionDetails.toDTO(),
          answerJson: this.answerJson,
          isFirstQuestion: this.isFirstQuestion,
          progressPercentage: this.progressPercentage.toDTO(),
      };
      if (this.submitConfirmationButtonText !== undefined && this.submitConfirmationButtonText !== null){
        dto.submitConfirmationButtonText = this.submitConfirmationButtonText.toDTO();
      }
      return dto;
    }
    private static validateAnswerJson = (answerJson: string): void => {
      if(answerJson.trim().length === 0) {
        throw new InvalidResponseAnswerJsonNonEmptyError(
          `Attribute answerJson cannot be an empty string.`
        );
      }
    }
    
  }
  
  Response.prototype.toString = function toString(): string {
    return `BacktrackToFirstUnansweredQuestion(respondentQuestionDetails='${this.respondentQuestionDetails}',answerJson='${this.answerJson}',isFirstQuestion='${this.isFirstQuestion}',progressPercentage='${this.progressPercentage}',submitConfirmationButtonText='${this.submitConfirmationButtonText}')`;
  };
  

  export enum RPCError {
    InvalidSurveyLinkId = "INVALID_SURVEY_LINK_ID",
    SurveyIsClosed = "SURVEY_IS_CLOSED",
    SurveyAlreadySubmitted = "SURVEY_ALREADY_SUBMITTED",
    UnansweredQuestionNotFound = "UNANSWERED_QUESTION_NOT_FOUND"
  }

  export namespace Errors {
    
    export interface InvalidSurveyLinkIdDTO {
      code: string;
    }
    
    export class InvalidSurveyLinkId extends LeoRPCError<RPCError> {
      readonly isInvalidSurveyLinkId: boolean = true;
    
    
      constructor(
      ) {
        const code = RPCError.InvalidSurveyLinkId;
        super(code);
      }
    
    
      toDTO(): InvalidSurveyLinkIdDTO {
        const dto: InvalidSurveyLinkIdDTO = {
          code: this.code,
        };
        return dto;
      }
    
      }
    
    InvalidSurveyLinkId.prototype.toString = function toString(): string {
      return `BacktrackToFirstUnansweredQuestionRPC.INVALID_SURVEY_LINK_ID()`;
    };
    
    
    export interface SurveyIsClosedDTO {
      code: string;
      surveyIsClosedTitleText: SurveyLocalizedTextDTO;
      surveyIsClosedDescriptionText: SurveyLocalizedTextDTO;
    }
    
    export class SurveyIsClosed extends LeoRPCError<RPCError> {
      readonly isSurveyIsClosed: boolean = true;
    
      readonly surveyIsClosedTitleText: SurveyLocalizedText;
      readonly surveyIsClosedDescriptionText: SurveyLocalizedText;
    
      constructor(
      surveyIsClosedTitleText: SurveyLocalizedText, 
      surveyIsClosedDescriptionText: SurveyLocalizedText
      ) {
        const code = RPCError.SurveyIsClosed;
        super(code);
        this.surveyIsClosedTitleText = surveyIsClosedTitleText;
        this.surveyIsClosedDescriptionText = surveyIsClosedDescriptionText;
      }
    
      static fromDTO(dto: SurveyIsClosedDTO): SurveyIsClosed {
        const surveyIsClosedTitleText = SurveyLocalizedText.fromDTO(getObject(dto, "surveyIsClosedTitleText", InvalidResponseSurveyIsClosedSurveyIsClosedTitleTextError));
        const surveyIsClosedDescriptionText = SurveyLocalizedText.fromDTO(getObject(dto, "surveyIsClosedDescriptionText", InvalidResponseSurveyIsClosedSurveyIsClosedDescriptionTextError));
        return new SurveyIsClosed(
          surveyIsClosedTitleText,
          surveyIsClosedDescriptionText
        );
      }
    
      toDTO(): SurveyIsClosedDTO {
        const dto: SurveyIsClosedDTO = {
          code: this.code,
          surveyIsClosedTitleText: this.surveyIsClosedTitleText.toDTO(),
          surveyIsClosedDescriptionText: this.surveyIsClosedDescriptionText.toDTO()
        };
        return dto;
      }
    
      }
    
    SurveyIsClosed.prototype.toString = function toString(): string {
      return `BacktrackToFirstUnansweredQuestionRPC.SURVEY_IS_CLOSED(surveyIsClosedTitleText='${this.surveyIsClosedTitleText}',surveyIsClosedDescriptionText='${this.surveyIsClosedDescriptionText}')`;
    };
    
    
    export interface SurveyAlreadySubmittedDTO {
      code: string;
      thankYouPageText: SurveyLocalizedTextDTO;
    }
    
    export class SurveyAlreadySubmitted extends LeoRPCError<RPCError> {
      readonly isSurveyAlreadySubmitted: boolean = true;
    
      readonly thankYouPageText: SurveyLocalizedText;
    
      constructor(
      thankYouPageText: SurveyLocalizedText
      ) {
        const code = RPCError.SurveyAlreadySubmitted;
        super(code);
        this.thankYouPageText = thankYouPageText;
      }
    
      static fromDTO(dto: SurveyAlreadySubmittedDTO): SurveyAlreadySubmitted {
        const thankYouPageText = SurveyLocalizedText.fromDTO(getObject(dto, "thankYouPageText", InvalidResponseSurveyAlreadySubmittedThankYouPageTextError));
        return new SurveyAlreadySubmitted(
          thankYouPageText
        );
      }
    
      toDTO(): SurveyAlreadySubmittedDTO {
        const dto: SurveyAlreadySubmittedDTO = {
          code: this.code,
          thankYouPageText: this.thankYouPageText.toDTO()
        };
        return dto;
      }
    
      }
    
    SurveyAlreadySubmitted.prototype.toString = function toString(): string {
      return `BacktrackToFirstUnansweredQuestionRPC.SURVEY_ALREADY_SUBMITTED(thankYouPageText='${this.thankYouPageText}')`;
    };
    
    
    export interface UnansweredQuestionNotFoundDTO {
      code: string;
    }
    
    export class UnansweredQuestionNotFound extends LeoRPCError<RPCError> {
      readonly isUnansweredQuestionNotFound: boolean = true;
    
    
      constructor(
      ) {
        const code = RPCError.UnansweredQuestionNotFound;
        super(code);
      }
    
    
      toDTO(): UnansweredQuestionNotFoundDTO {
        const dto: UnansweredQuestionNotFoundDTO = {
          code: this.code,
        };
        return dto;
      }
    
      }
    
    UnansweredQuestionNotFound.prototype.toString = function toString(): string {
      return `BacktrackToFirstUnansweredQuestionRPC.UNANSWERED_QUESTION_NOT_FOUND()`;
    };
    

    export type Errors = InvalidSurveyLinkId | SurveyIsClosed | SurveyAlreadySubmitted | UnansweredQuestionNotFound;
    export type ErrorsDTO = InvalidSurveyLinkIdDTO | SurveyIsClosedDTO | SurveyAlreadySubmittedDTO | UnansweredQuestionNotFoundDTO;

    export const fromDTO = (dto: ErrorsDTO): Errors => {
      switch (dto.code) {
        case RPCError.InvalidSurveyLinkId:
          return new InvalidSurveyLinkId();
        case RPCError.SurveyIsClosed:
          return SurveyIsClosed.fromDTO(dto as SurveyIsClosedDTO);
        case RPCError.SurveyAlreadySubmitted:
          return SurveyAlreadySubmitted.fromDTO(dto as SurveyAlreadySubmittedDTO);
        case RPCError.UnansweredQuestionNotFound:
          return new UnansweredQuestionNotFound();
        default:
          throw new InvalidResponseError(`Unexpected error code: ${dto.code}`);
      }
    };
  }
}

export class InvalidRequestSurveyLinkIdError extends BacktrackToFirstUnansweredQuestionRPC.InvalidRequestError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidRequestSurveyLinkIdError";
  }
}

export class InvalidResponseRespondentQuestionDetailsError extends BacktrackToFirstUnansweredQuestionRPC.InvalidResponseError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidResponseRespondentQuestionDetailsError";
  }
}

export class InvalidResponseAnswerJsonError extends BacktrackToFirstUnansweredQuestionRPC.InvalidResponseError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidResponseAnswerJsonError";
  }
}

export class InvalidResponseAnswerJsonNonEmptyError extends InvalidResponseAnswerJsonError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidResponseAnswerJsonNonEmptyError";
  }
}

export class InvalidResponseIsFirstQuestionError extends BacktrackToFirstUnansweredQuestionRPC.InvalidResponseError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidResponseIsFirstQuestionError";
  }
}

export class InvalidResponseProgressPercentageError extends BacktrackToFirstUnansweredQuestionRPC.InvalidResponseError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidResponseProgressPercentageError";
  }
}

export class InvalidResponseSubmitConfirmationButtonTextError extends BacktrackToFirstUnansweredQuestionRPC.InvalidResponseError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidResponseSubmitConfirmationButtonTextError";
  }
}

export class InvalidResponseInvalidSurveyLinkIdError extends BacktrackToFirstUnansweredQuestionRPC.InvalidResponseError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidResponseInvalidSurveyLinkIdError";
  }
}

export class InvalidResponseSurveyIsClosedError extends BacktrackToFirstUnansweredQuestionRPC.InvalidResponseError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidResponseSurveyIsClosedError";
  }
}

export class InvalidResponseSurveyIsClosedSurveyIsClosedTitleTextError extends InvalidResponseSurveyIsClosedError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidResponseSurveyIsClosedSurveyIsClosedTitleTextError";
  }
}

export class InvalidResponseSurveyIsClosedSurveyIsClosedDescriptionTextError extends InvalidResponseSurveyIsClosedError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidResponseSurveyIsClosedSurveyIsClosedDescriptionTextError";
  }
}
export class InvalidResponseSurveyAlreadySubmittedError extends BacktrackToFirstUnansweredQuestionRPC.InvalidResponseError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidResponseSurveyAlreadySubmittedError";
  }
}

export class InvalidResponseSurveyAlreadySubmittedThankYouPageTextError extends InvalidResponseSurveyAlreadySubmittedError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidResponseSurveyAlreadySubmittedThankYouPageTextError";
  }
}
export class InvalidResponseUnansweredQuestionNotFoundError extends BacktrackToFirstUnansweredQuestionRPC.InvalidResponseError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidResponseUnansweredQuestionNotFoundError";
  }
}

